import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import ModalConstants from 'app/shared/constants/ModalConstants';
import ModalActions from 'app/shared/actions/ModalActions';
import BillingActions from 'app/shared/actions/BillingActions';

import billingUtils from 'app/shared/utils/billingUtils';

import {
    TextButton,
    Text,
    Paragraph,
    Spacer,
} from '@zillow/constellation';

import { RMWContext } from 'app/shared/providers/RMWContext';

import { useParams } from 'react-router-dom';

import { StyledCircle } from './StyledComponents';

const AutopayStatusWidget = ({ accountNumber, disabled, hideSetup }) => {
    const { paths } = React.useContext(RMWContext);
    const { invoiceId } = useParams();

    const dispatch = useDispatch();
    const isAutopayEnabled = useSelector(state => state.billing.accountPaymentMethods.isAutopayEnabled);

    const cancelAutoPay = () => {
        dispatch(ModalActions.hideModal());
        dispatch(BillingActions.setPaymentMethodLock(true));
        return dispatch(BillingActions.removeAutopay({
            accountNumber,
        })).catch(() => {
            // Todo handle error
        });
    };

    const handleCancelAutoPay = () => {
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.CONFIRMATION_MODAL,
            header: 'Are you sure you want to cancel auto pay?',
            submitButtonTitle: 'Keep Auto Pay',
            cancelButtonTitle: 'Cancel Auto Pay',
            textContent: 'Auto pay lets you avoid the risk of missed payments. '
                + 'If you cancel, you’ll have to pay manually each month.',
            onCancel: cancelAutoPay,
            onSubmit: () => dispatch(ModalActions.hideModal()),
        }));
    };

    const handleEditClick = () => {
        const url = `${paths.billingPage}/account/${accountNumber}`;
        const target = `${url}?prev=invoicePayment&invoiceNumber=${invoiceId}`;
        window.router.transitionTo(target);
    };

    const nextPaymentDate = billingUtils.getNextAutoPaymentDate();

    const renderAutopayOn = () => (
        <>
            <Spacer marginBottom="xs">
                <Text fontType="bodySmallHeading">
                    {`Next Payment Date: ${nextPaymentDate}`}
                </Text>
            </Spacer>
            <Paragraph>
                {`We will make payments to this account automatically on the next paymnet date.
                 Any changes for the current month must be made by ${nextPaymentDate} to avoid auto payment.`}
            </Paragraph>
            <Spacer marginY="sm">
                {!disabled && <TextButton onClick={handleCancelAutoPay}>Turn Autopay Off</TextButton>}
            </Spacer>
        </>
    );

    const renderAutopayOff = () => (
        <>
            <Paragraph>Turn on autopay after adding a payment method that can be associated with it.</Paragraph>
            <Spacer marginY="sm">
                {!disabled && !hideSetup && <TextButton onClick={handleEditClick}>Set up autopay</TextButton>}
            </Spacer>
        </>
    );

    return (
        <Spacer marginY="md">
            <Spacer marginBottom="sm">
                <StyledCircle enabled={isAutopayEnabled} />
                <Text as="strong">
                    Automatic payments is
                    {' '}
                    {isAutopayEnabled ? 'ON' : 'OFF'}
                </Text>
            </Spacer>
            {isAutopayEnabled ? renderAutopayOn() : renderAutopayOff()}
        </Spacer>
    );
};

export default AutopayStatusWidget;
